import * as Yup from 'yup'

import { useFormik } from 'formik'
import { useMutation } from '@apollo/client'
import {
  BeginLoginResult,
  IResBeginPasskeyAuthentication,
  IResBeginLogin,
  IResCompletePasskeyAuthentication,
  IResCompleteLogin,
  IResSendEmailTwoFactorCode,
  TokenAuth,
} from '../../types/auth'
import {
  BEGIN_PASSKEY_AUTHENTICATION,
  BEGIN_LOGIN,
  COMPLETE_PASSKEY_AUTHENTICATION,
  COMPLETE_LOGIN,
  SEND_EMAIL_TWO_FACTOR_CODE,
} from '../../gql/mutations/authMutations'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useGetUser } from '../../store/userStore'
import { STORAGE_NAMES, VERSION_APP } from '../../helpers/consts'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  getPasskeyAssertion,
  getPasskeyErrorMessage,
  getPasskeyUnavailableMessage,
  isPasskeySupported,
} from '../../helpers/passkeys'
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion'
import { Mail, Lock, Eye, EyeOff, Fingerprint, ShieldCheck } from 'lucide-react'
import {defineMessages, IntlShape, useIntl} from 'react-intl'
import {LanguageToggle} from '../../../../_metronic/partials/layout/header-menus/LanguageToggle'

const M = defineMessages({
  stepSignIn: {id: 'auth.login.step.signIn', defaultMessage: 'Sign in'},
  stepEmail: {
    id: 'auth.login.step.emailVerification',
    defaultMessage: 'Email verification',
  },
  stepAuthenticator: {
    id: 'auth.login.step.authenticatorVerification',
    defaultMessage: 'Authenticator verification',
  },

  welcome: {id: 'auth.login.welcome', defaultMessage: 'Welcome back to Policy Tools!'},
  subtitle: {
    id: 'auth.login.subtitle',
    defaultMessage: 'The agile platform for your enterprise policies.',
  },
  formAria: {id: 'auth.login.formAria', defaultMessage: 'Sign in form'},
  emailPlaceholder: {id: 'auth.login.emailPlaceholder', defaultMessage: 'you@company.com'},
  forgot: {id: 'auth.login.forgot', defaultMessage: 'I forgot my password'},
  or: {id: 'auth.login.or', defaultMessage: 'or'},

  tfaCodeSentTo: {
    id: 'auth.login.tfa.codeSentTo',
    defaultMessage: 'Code sent to {destination}',
  },
  tfaYourEmail: {id: 'auth.login.tfa.yourEmail', defaultMessage: 'your email'},
  tfaOpenApp: {
    id: 'auth.login.tfa.openApp',
    defaultMessage: 'Open your authenticator app',
  },
  tfaSub: {
    id: 'auth.login.tfa.sub',
    defaultMessage: "Your password was accepted. One more step to verify it's you.",
  },
  tfaEmailCode: {id: 'auth.login.tfa.emailCode', defaultMessage: 'Email code'},
  tfaAuthenticatorCode: {
    id: 'auth.login.tfa.authenticatorCode',
    defaultMessage: 'Authenticator code',
  },
  tfaSendByEmail: {
    id: 'auth.login.tfa.sendByEmail',
    defaultMessage: "Can't access authenticator app? Send code by email.",
  },
  tfaUseApp: {id: 'auth.login.tfa.useApp', defaultMessage: 'Use authenticator app instead'},
  tfaResend: {id: 'auth.login.tfa.resend', defaultMessage: 'Resend email code'},
  tfaUseAnotherAccount: {
    id: 'auth.login.tfa.useAnotherAccount',
    defaultMessage: 'Use another account',
  },

  submitVerify: {id: 'auth.login.submit.verify', defaultMessage: 'Verify and continue'},
  submitContinue: {id: 'common.action.continue', defaultMessage: 'Continue'},
  passkeySignIn: {id: 'auth.login.passkey.signIn', defaultMessage: 'Sign in with passkey'},

  // Errores y avisos
  invalidCredentials: {
    id: 'auth.login.error.invalidCredentials',
    defaultMessage: 'Please enter valid credentials',
  },
  mustChangePassword: {
    id: 'auth.login.error.mustChangePassword',
    defaultMessage: 'You must change your password to continue.',
  },
  tfaSetupRequired: {
    id: 'auth.login.tfa.setupRequired',
    defaultMessage: 'Two-factor authentication setup is required for your account.',
  },
  unableToStart: {
    id: 'auth.login.error.unableToStart',
    defaultMessage: 'Unable to start login. Please try again.',
  },
  tfaRequired: {
    id: 'auth.login.tfa.required',
    defaultMessage: 'Two-factor authentication is required.',
  },
  unableToContinue: {
    id: 'auth.login.error.unableToContinue',
    defaultMessage: 'Unable to continue the login flow.',
  },
  codeRequired: {
    id: 'auth.login.error.codeRequired',
    defaultMessage: 'Verification code is required',
  },
  unableToComplete: {
    id: 'auth.login.error.unableToComplete',
    defaultMessage: 'Unable to complete login. Please try again.',
  },
  // Se lanza con `new Error` y su .message acaba en un toast: es interfaz (§17.bis).
  emailCodeFailed: {
    id: 'auth.login.error.emailCodeFailed',
    defaultMessage: 'Unable to send the email verification code.',
  },
  waitBeforeResend: {
    id: 'auth.login.error.waitBeforeResend',
    defaultMessage: 'Please wait before requesting another code.',
  },
  tfaCodeSent: {
    id: 'auth.login.tfa.codeSent',
    defaultMessage: 'Verification code sent by email.',
  },
  passkeyEmailRequired: {
    id: 'auth.login.passkey.emailRequired',
    defaultMessage: 'Email is required to use passkey sign-in.',
  },
  passkeyNotAvailable: {
    id: 'auth.login.passkey.notAvailable',
    defaultMessage: 'Passkey sign-in is not available for this account.',
  },
  passkeyUnableComplete: {
    id: 'auth.login.passkey.unableComplete',
    defaultMessage: 'Unable to complete passkey sign-in.',
  },
  passkeySignInError: {
    id: 'auth.login.passkey.error.signIn',
    defaultMessage: 'Unable to sign in with passkey.',
  },

  // Compartidas por las tres pantallas de auth
  genericError: {
    id: 'auth.error.generic',
    defaultMessage: 'An error occurred. Please try again.',
  },

  // Reusadas
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  password: {id: 'admin.users.field.password', defaultMessage: 'Password'},
  showPassword: {id: 'admin.oneUser.personal.showPassword', defaultMessage: 'Show password'},
  hidePassword: {id: 'admin.oneUser.personal.hidePassword', defaultMessage: 'Hide password'},
  pleaseWait: {id: 'admin.oneUser.pleaseWait', defaultMessage: 'Please wait...'},

  // Validacion
  emailInvalid: {id: 'validation.email.invalid', defaultMessage: 'Invalid email'},
  emailRequired: {id: 'validation.email.required', defaultMessage: 'Email is required'},
  passwordRequired: {id: 'validation.password.required', defaultMessage: 'Password is required'},
  minSymbols: {id: 'validation.minSymbols', defaultMessage: 'Minimum {min} symbols'},
  maxSymbols: {id: 'validation.maxSymbols', defaultMessage: 'Maximum {max} symbols'},
})

// i18n-ok-start: contrato con la API y simbolos de relleno, no interfaz.
// El backend devuelve este mensaje literal y aqui se COMPARA para decidir si hay
// que forzar el cambio de contraseña: traducirlo romperia el flujo en español.
const BACKEND_FORCE_PASSWORD_CHANGE = 'You must change your password before signing in.'
const BACKEND_INVALID_CREDENTIALS = 'Please enter valid credentials'
const PASSWORD_MASK = '••••••••'
const CODE_MASK = '000000'
const ARROW = '→'
const BRAND_POLICY_TOOLS = 'Policy Tools'
// i18n-ok-end

const FIELD_MIN = 3
const FIELD_MAX = 50

// El esquema necesita intl, asi que no puede vivir a nivel de modulo (§17.8).
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage(M.emailInvalid))
      .min(FIELD_MIN, intl.formatMessage(M.minSymbols, {min: FIELD_MIN}))
      .max(FIELD_MAX, intl.formatMessage(M.maxSymbols, {max: FIELD_MAX}))
      .required(intl.formatMessage(M.emailRequired)),
    password: Yup.string()
      .min(FIELD_MIN, intl.formatMessage(M.minSymbols, {min: FIELD_MIN}))
      .max(FIELD_MAX, intl.formatMessage(M.maxSymbols, {max: FIELD_MAX}))
      .required(intl.formatMessage(M.passwordRequired)),
  })

// Devuelve texto que se pinta, asi que recibe intl (§17.bis).
const getErrorMessage = (intl: IntlShape, error: any) => {
  let message = intl.formatMessage(M.genericError)

  if (error?.message?.includes(BACKEND_INVALID_CREDENTIALS)) {
    return intl.formatMessage(M.invalidCredentials)
  }

  if (error?.graphQLErrors?.length > 0) {
    return error.graphQLErrors[0].message
  }

  if (typeof error?.message === 'string') {
    return error.message.replace(/ApolloError.*/s, '').trim() || message
  }

  return message
}

export const LoginPage = () => {
  const intl = useIntl()
  const [gqlBeginLogin, { loading: loadingBegin }] = useMutation<IResBeginLogin>(BEGIN_LOGIN)
  const [gqlCompleteLogin, { loading: loadingComplete }] =
    useMutation<IResCompleteLogin>(COMPLETE_LOGIN)
  const [gqlSendEmailCode, { loading: loadingEmailCode }] =
    useMutation<IResSendEmailTwoFactorCode>(SEND_EMAIL_TWO_FACTOR_CODE)
  const [gqlBeginPasskeyAuthentication, { loading: loadingPasskeyBegin }] =
    useMutation<IResBeginPasskeyAuthentication>(BEGIN_PASSKEY_AUTHENTICATION)
  const [gqlCompletePasskeyAuthentication, { loading: loadingPasskeyComplete }] =
    useMutation<IResCompletePasskeyAuthentication>(COMPLETE_PASSKEY_AUTHENTICATION)

  const login = useGetUser((s) => s.login)
  const navigate = useNavigate()
  const location = useLocation()

  const [showPass, setShowPass] = useState(false)
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [availableMethods, setAvailableMethods] = useState<string[]>([])
  const [selectedMethod, setSelectedMethod] = useState<'APP' | 'EMAIL'>('APP')
  const [emailDestination, setEmailDestination] = useState<string | null>(null)
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const passkeySupported = useMemo(() => isPasskeySupported(), [])

  const isTwoFactorStep = Boolean(challengeId)
  const canUseEmailFallback = availableMethods.includes('EMAIL')
  const loading =
    loadingBegin || loadingComplete || loadingEmailCode || loadingPasskeyBegin || loadingPasskeyComplete

  const stepTitle = useMemo(() => {
    if (!isTwoFactorStep) return intl.formatMessage(M.stepSignIn)
    return intl.formatMessage(selectedMethod === 'EMAIL' ? M.stepEmail : M.stepAuthenticator)
  }, [intl, isTwoFactorStep, selectedMethod])

  const resetTwoFactorState = () => {
    setChallengeId(null)
    setAvailableMethods([])
    setSelectedMethod('APP')
    setEmailDestination(null)
    setTwoFactorCode('')
  }

  const handleAuthError = (error: any, setStatus: (message: string | null) => void) => {
    const message = getErrorMessage(intl, error)
    const requiresPasswordChange = message.includes(BACKEND_FORCE_PASSWORD_CHANGE)

    if (requiresPasswordChange) {
      const emailQuery = encodeURIComponent(formik.values.email || '')
      toast.info(intl.formatMessage(M.mustChangePassword))
      navigate(`/reset-password?forced=1&email=${emailQuery}`)
      return
    }

    toast.error(message)
    setStatus(message)
  }

  const handleSuccessfulLogin = (auth: TokenAuth) => {
    login(auth)

    if (auth.user?.twoFactorRequired && !auth.user?.twoFactorEnabled) {
      toast.info(intl.formatMessage(M.tfaSetupRequired))
      navigate('/profile', { replace: true })
      return
    }

    const url = (location?.state as { prevUrl: string })?.prevUrl || '/'
    navigate(url)
  }

  const handleBeginLoginResult = (result?: BeginLoginResult | null, setStatus?: (message: string | null) => void) => {
    if (!result) {
      const message = intl.formatMessage(M.unableToStart)
      toast.error(message)
      setStatus?.(message)
      return
    }

    if (result.auth) {
      handleSuccessfulLogin(result.auth)
      return
    }

    if (result.requiresTwoFactor && result.challengeId) {
      setChallengeId(result.challengeId)
      setAvailableMethods(result.availableMethods || ['APP'])
      setSelectedMethod('APP')
      setEmailDestination(result.emailDestination || null)
      setTwoFactorCode('')
      setStatus?.(null)
      toast.info(result.message || intl.formatMessage(M.tfaRequired))
      return
    }

    const fallbackMessage = result.message || intl.formatMessage(M.unableToContinue)
    toast.error(fallbackMessage)
    setStatus?.(fallbackMessage)
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (values, { setStatus }) => {
      setStatus(null)

      if (isTwoFactorStep) {
        if (!twoFactorCode.trim()) {
          setStatus(intl.formatMessage(M.codeRequired))
          return
        }

        try {
          const response = await gqlCompleteLogin({
            variables: {
              challengeId: challengeId!,
              code: twoFactorCode.trim(),
              method: selectedMethod,
            },
          })

          const auth = response.data?.completeLogin?.auth
          if (!auth) {
            const message =
              response.data?.completeLogin?.message || intl.formatMessage(M.unableToComplete)
            toast.error(message)
            setStatus(message)
            return
          }

          handleSuccessfulLogin(auth)
        } catch (error) {
          handleAuthError(error, setStatus)
        }

        return
      }

      try {
        const response = await gqlBeginLogin({
          variables: {
            email: values.email,
            password: values.password,
          },
        })

        handleBeginLoginResult(response.data?.beginLogin, setStatus)
      } catch (error) {
        handleAuthError(error, setStatus)
      }
    },
  })

  const handleSendEmailCode = async () => {
    if (!challengeId) return

    formik.setStatus(null)

    try {
      const response = await gqlSendEmailCode({
        variables: {
          challengeId: challengeId!,
        },
      })

      const result = response.data?.sendEmailTwoFactorCode
      if (!result) {
        throw new Error(intl.formatMessage(M.emailCodeFailed))
      }

      if (!result.success) {
        const waitMessage = result.message || intl.formatMessage(M.waitBeforeResend)
        toast.info(waitMessage)
        formik.setStatus(waitMessage)
        return
      }

      setSelectedMethod('EMAIL')
      setEmailDestination(result.emailDestination || emailDestination)
      setTwoFactorCode('')
      toast.success(result.message || intl.formatMessage(M.tfaCodeSent))
    } catch (error) {
      handleAuthError(error, formik.setStatus)
    }
  }

  const handlePasskeySignIn = async () => {
    const normalizedEmail = formik.values.email.trim().toLowerCase()

    if (!normalizedEmail) {
      formik.setFieldTouched('email', true)
      formik.setStatus(intl.formatMessage(M.passkeyEmailRequired))
      return
    }

    if (!passkeySupported) {
      const message = getPasskeyUnavailableMessage(intl)
      toast.info(message)
      formik.setStatus(message)
      return
    }

    formik.setStatus(null)

    try {
      const beginResponse = await gqlBeginPasskeyAuthentication({
        variables: {
          email: normalizedEmail,
        },
      })

      const result = beginResponse.data?.beginPasskeyAuthentication
      if (!result?.success || !result.challengeId || !result.publicKeyOptions) {
        const message = result?.message || intl.formatMessage(M.passkeyNotAvailable)
        toast.error(message)
        formik.setStatus(message)
        return
      }

      const credential = await getPasskeyAssertion(result.publicKeyOptions)
      const completeResponse = await gqlCompletePasskeyAuthentication({
        variables: {
          challengeId: result.challengeId,
          credential,
        },
      })

      const completeResult = completeResponse.data?.completePasskeyAuthentication
      if (!completeResult?.success || !completeResult?.auth) {
        const message = completeResult?.message || intl.formatMessage(M.passkeyUnableComplete)
        toast.error(message)
        formik.setStatus(message)
        return
      }

      handleSuccessfulLogin(completeResult.auth)
    } catch (error) {
      const message = getPasskeyErrorMessage(
        intl,
        error,
        intl.formatMessage(M.passkeySignInError)
      )
      const requiresPasswordChange = message.includes(BACKEND_FORCE_PASSWORD_CHANGE)

      if (requiresPasswordChange) {
        const emailQuery = encodeURIComponent(normalizedEmail)
        toast.info(intl.formatMessage(M.mustChangePassword))
        navigate(`/reset-password?forced=1&email=${emailQuery}`)
        return
      }

      toast.error(message)
      formik.setStatus(message)
    }
  }

  // ── RingCentral widget ──────────────────────────────────────────────────────
  useEffect(() => {
    const $div = document.querySelector('#rc-widget') as HTMLDivElement
    if (!$div) return
    const str = localStorage.getItem(STORAGE_NAMES.RING_CENTRAL)
    if (str) {
      $div.style.opacity = '0'
      $div.style.pointerEvents = 'none'
    }

    return () => {
      if ($div) {
        $div.style.opacity = '1'
        $div.style.pointerEvents = 'initial'
      }
    }
  }, [])

  // ── Motion / animation setup ────────────────────────────────────────────────
  const prefersReducedMotion = useReducedMotion()
  const containerRef = useRef<HTMLDivElement>(null)
  const GLOW_SIZE = 700

  const rawX = useMotionValue(0)
  const rawY = useMotionValue(0)
  const springCfg = { stiffness: 70, damping: 22, mass: 0.6 }
  const smoothX = useSpring(rawX, springCfg)
  const smoothY = useSpring(rawY, springCfg)

  const glowX = useTransform(smoothX, (v) => v - GLOW_SIZE / 2)
  const glowY = useTransform(smoothY, (v) => v - GLOW_SIZE / 2)

  const p1x = useTransform(smoothX, (v) => (v - (containerRef.current?.clientWidth ?? 1440) / 2) * 0.042)
  const p1y = useTransform(smoothY, (v) => (v - (containerRef.current?.clientHeight ?? 900) / 2) * 0.028)
  const p2x = useTransform(smoothX, (v) => (v - (containerRef.current?.clientWidth ?? 1440) / 2) * 0.022)
  const p2y = useTransform(smoothY, (v) => (v - (containerRef.current?.clientHeight ?? 900) / 2) * 0.015)
  const p3x = useTransform(smoothX, (v) => (v - (containerRef.current?.clientWidth ?? 1440) / 2) * 0.010)
  const p3y = useTransform(smoothY, (v) => (v - (containerRef.current?.clientHeight ?? 900) / 2) * 0.007)

  const particles = useMemo(() =>
    Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: (i * 137.508) % 100,
      y: (i * 97.333 + i * 0.7) % 100,
      size: 1.5 + (i % 5) * 0.6,
      opacity: 0.13 + (i % 7) * 0.055,
      layer: i % 3,
      accent: i % 5 === 0,
    }))
    , [])

  const tiltX = useTransform(smoothY, (v) => {
    const h = containerRef.current?.clientHeight ?? 900
    return ((v - h / 2) / (h / 2)) * -4
  })
  const tiltY = useTransform(smoothX, (v) => {
    const w = containerRef.current?.clientWidth ?? 1440
    return ((v - w / 2) / (w / 2)) * 4
  })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    rawX.set(el.clientWidth / 2)
    rawY.set(el.clientHeight / 2)
  }, [rawX, rawY])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (prefersReducedMotion) return
      const rect = e.currentTarget.getBoundingClientRect()
      rawX.set(e.clientX - rect.left)
      rawY.set(e.clientY - rect.top)
    },
    [prefersReducedMotion, rawX, rawY]
  )

  const handleMouseLeave = useCallback(() => {
    if (prefersReducedMotion || !containerRef.current) return
    rawX.set(containerRef.current.clientWidth / 2)
    rawY.set(containerRef.current.clientHeight / 2)
  }, [prefersReducedMotion, rawX, rawY])

  const transition = prefersReducedMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] }

  const cardVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 32, scale: prefersReducedMotion ? 1 : 0.97 },
    visible: { opacity: 1, y: 0, scale: 1, transition },
  }

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: prefersReducedMotion ? 0 : 0.07 } },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 12 },
    visible: { opacity: 1, y: 0, transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.35 } },
  }

  const stepVariants = {
    enter: { opacity: 0, x: prefersReducedMotion ? 0 : 30 },
    center: { opacity: 1, x: 0, transition },
    exit: { opacity: 0, x: prefersReducedMotion ? 0 : -30, transition: { ...transition, duration: 0.25 } },
  }

  // El glifo va pegado a la palabra: se arma fuera del JSX porque el rule marca
  // tambien los template literals (§17.4).
  const continueLabel = `${intl.formatMessage(M.submitContinue)} ${ARROW}`

  const isSubmitDisabled =
    formik.isSubmitting ||
    loading ||
    (!isTwoFactorStep && !formik.isValid) ||
    (isTwoFactorStep && !twoFactorCode.trim())

  return (
    <div
      className='login-revamp-root'
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Background ─────────────────────────────────────────────────────── */}
      <div className='lr-aurora' aria-hidden='true' />
      <div className='lr-dots' aria-hidden='true' />
      <div className='lr-blob lr-blob--1' aria-hidden='true' />
      <div className='lr-blob lr-blob--2' aria-hidden='true' />
      <div className='lr-blob lr-blob--3' aria-hidden='true' />
      <motion.div className='lr-cursor-glow' style={{ x: glowX, y: glowY }} aria-hidden='true' />

      {/* ── Parallax particle layers ────────────────────────────────────────── */}
      {!prefersReducedMotion && (
        <>
          <motion.div className='lr-particles' style={{ x: p1x, y: p1y }} aria-hidden='true'>
            {particles.filter(p => p.layer === 0).map(p => (
              <span key={p.id} className={`lr-particle${p.accent ? ' lr-particle--accent' : ''}`}
                style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity: p.opacity }} />
            ))}
          </motion.div>
          <motion.div className='lr-particles' style={{ x: p2x, y: p2y }} aria-hidden='true'>
            {particles.filter(p => p.layer === 1).map(p => (
              <span key={p.id} className={`lr-particle${p.accent ? ' lr-particle--accent' : ''}`}
                style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity: p.opacity }} />
            ))}
          </motion.div>
          <motion.div className='lr-particles' style={{ x: p3x, y: p3y }} aria-hidden='true'>
            {particles.filter(p => p.layer === 2).map(p => (
              <span key={p.id} className={`lr-particle${p.accent ? ' lr-particle--accent' : ''}`}
                style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, opacity: p.opacity }} />
            ))}
          </motion.div>
        </>
      )}

      {/* ── Page content ───────────────────────────────────────────────────── */}
      <div className='lr-page'>

        {/* Heading: logo + title + subtitle */}
        <motion.div
          className='lr-heading'
          variants={containerVariants}
          initial='hidden'
          animate='visible'
        >
          <motion.img
            src='/media/tfi/logo_policytools.png'
            alt={BRAND_POLICY_TOOLS}
            className='lr-logo'
            variants={itemVariants}
          />
          <motion.h1 className='lr-page-title' variants={itemVariants}>
            {intl.formatMessage(M.welcome)}
          </motion.h1>
          <motion.p className='lr-page-subtitle' variants={itemVariants}>
            {intl.formatMessage(M.subtitle)}
          </motion.p>
        </motion.div>

        {/* Split card */}
        <motion.div
          className='lr-card'
          variants={cardVariants}
          initial='hidden'
          animate='visible'
          style={prefersReducedMotion ? {} : { rotateX: tiltX, rotateY: tiltY }}
        >
          {/* ── Left: form ───────────────────────────────────────────────── */}
          <div className='lr-card-form'>
            <form
              onSubmit={formik.handleSubmit}
              noValidate
              id='kt_login_signin_form'
              aria-label={intl.formatMessage(M.formAria)}
            >
              {/* Step label */}
              <AnimatePresence mode='wait'>
                <motion.span
                  key={stepTitle}
                  className='lr-step-label'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
                >
                  {stepTitle}
                </motion.span>
              </AnimatePresence>

              {/* Error alert */}
              <AnimatePresence>
                {formik.status && (
                  <motion.div
                    className='lr-alert'
                    role='alert'
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: 'auto', marginBottom: 20 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.22 }}
                  >
                    {formik.status}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Step content */}
              <AnimatePresence mode='wait'>
                {!isTwoFactorStep ? (
                  <motion.div key='step-login' variants={stepVariants} initial='enter' animate='center' exit='exit'>
                    <motion.div variants={containerVariants} initial='hidden' animate='visible'>

                      {/* Email */}
                      <motion.div className='lr-form-group' variants={itemVariants}>
                        <label className='lr-label' htmlFor='login-email'>
                          {intl.formatMessage(M.email)}
                        </label>
                        <div className='lr-input-wrap'>
                          <span className='lr-input-icon' aria-hidden='true'><Mail size={16} /></span>
                          <input
                            id='login-email' type='email'
                            placeholder={intl.formatMessage(M.emailPlaceholder)}
                            className={`lr-input${formik.touched.email && formik.errors.email ? ' lr-input--error' : ''}`}
                            {...formik.getFieldProps('email')} name='email' autoComplete='email'
                          />
                        </div>
                        {formik.touched.email && formik.errors.email && (
                          <p className='lr-error-msg' role='alert'>{formik.errors.email}</p>
                        )}
                      </motion.div>

                      {/* Password */}
                      <motion.div className='lr-form-group' variants={itemVariants}>
                        <label className='lr-label' htmlFor='login-password'>
                          {intl.formatMessage(M.password)}
                        </label>
                        <div className='lr-input-wrap'>
                          <span className='lr-input-icon' aria-hidden='true'><Lock size={16} /></span>
                          <input
                            id='login-password'
                            type={showPass ? 'text' : 'password'}
                            placeholder={PASSWORD_MASK}
                            className={`lr-input lr-input--pr${formik.touched.password && formik.errors.password ? ' lr-input--error' : ''}`}
                            autoComplete='current-password'
                            {...formik.getFieldProps('password')}
                          />
                          <button type='button' className='lr-toggle-btn'
                            onClick={() => setShowPass(p => !p)}
                            aria-label={intl.formatMessage(
                              showPass ? M.hidePassword : M.showPassword
                            )}
                          >
                            {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                          </button>
                        </div>
                        {formik.touched.password && formik.errors.password && (
                          <p className='lr-error-msg' role='alert'>{formik.errors.password}</p>
                        )}
                      </motion.div>

                      {/* Forgot password */}
                      <motion.div className='lr-forgot' variants={itemVariants}>
                        <Link to='/forgot-password'>{intl.formatMessage(M.forgot)}</Link>
                      </motion.div>

                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div key='step-2fa' variants={stepVariants} initial='enter' animate='center' exit='exit'>
                    <motion.div variants={containerVariants} initial='hidden' animate='visible'>

                      {/* 2FA info */}
                      <motion.div className='lr-tfa-info' variants={itemVariants}>
                        <p className='lr-tfa-info__title'>
                          {selectedMethod === 'EMAIL'
                            ? intl.formatMessage(M.tfaCodeSentTo, {
                                destination:
                                  emailDestination || intl.formatMessage(M.tfaYourEmail),
                              })
                            : intl.formatMessage(M.tfaOpenApp)}
                        </p>
                        <p className='lr-tfa-info__sub'>
                          {intl.formatMessage(M.tfaSub)}
                        </p>
                      </motion.div>

                      {/* Code input */}
                      <motion.div className='lr-form-group' variants={itemVariants}>
                        <label className='lr-label' htmlFor='login-2fa-code'>
                          {intl.formatMessage(
                            selectedMethod === 'EMAIL' ? M.tfaEmailCode : M.tfaAuthenticatorCode
                          )}
                        </label>
                        <div className='lr-input-wrap'>
                          <span className='lr-input-icon' aria-hidden='true'><ShieldCheck size={16} /></span>
                          <input
                            id='login-2fa-code' type='tel' placeholder={CODE_MASK}
                            inputMode='numeric' autoComplete='one-time-code'
                            value={twoFactorCode}
                            onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className='lr-input'
                          />
                        </div>
                      </motion.div>

                      {/* 2FA links */}
                      <motion.div className='lr-link-actions' variants={itemVariants}>
                        {canUseEmailFallback && selectedMethod !== 'EMAIL' && (
                          <button type='button' className='lr-link-btn' onClick={handleSendEmailCode} disabled={loading}>
                            {intl.formatMessage(M.tfaSendByEmail)}
                          </button>
                        )}
                        {selectedMethod === 'EMAIL' && (
                          <>
                            <button type='button' className='lr-link-btn'
                              onClick={() => { setSelectedMethod('APP'); setTwoFactorCode(''); formik.setStatus(null) }}
                              disabled={loading}>
                              {intl.formatMessage(M.tfaUseApp)}
                            </button>
                            <button type='button' className='lr-link-btn' onClick={handleSendEmailCode} disabled={loading}>
                              {intl.formatMessage(M.tfaResend)}
                            </button>
                          </>
                        )}
                        <button type='button' className='lr-link-btn lr-link-btn--muted'
                          onClick={() => { resetTwoFactorState(); formik.setStatus(null) }}
                          disabled={loading}>
                          {intl.formatMessage(M.tfaUseAnotherAccount)}
                        </button>
                      </motion.div>

                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Continue button */}
              <motion.button
                type='submit'
                id='kt_sign_in_submit'
                className='lr-btn-primary'
                disabled={isSubmitDisabled}
                whileHover={isSubmitDisabled || prefersReducedMotion ? {} : { scale: 1.02 }}
                whileTap={isSubmitDisabled || prefersReducedMotion ? {} : { scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <span className='lr-spinner' aria-hidden='true' />
                    {intl.formatMessage(M.pleaseWait)}
                  </>
                ) : isTwoFactorStep ? (
                  intl.formatMessage(M.submitVerify)
                ) : (
                  continueLabel
                )}
              </motion.button>

              {/* Passkey */}
              {!isTwoFactorStep && (
                <>
                  <div className='lr-divider'>{intl.formatMessage(M.or)}</div>
                  <motion.button
                    type='button' className='lr-btn-secondary'
                    onClick={handlePasskeySignIn} disabled={loading || !passkeySupported}
                    whileHover={loading || !passkeySupported || prefersReducedMotion ? {} : { scale: 1.01 }}
                    whileTap={loading || !passkeySupported || prefersReducedMotion ? {} : { scale: 0.99 }}
                  >
                    <Fingerprint size={18} aria-hidden='true' />
                    {intl.formatMessage(M.passkeySignIn)}
                  </motion.button>
                </>
              )}

              {!isTwoFactorStep && !passkeySupported && (
                <p className='lr-passkey-note'>{getPasskeyUnavailableMessage(intl)}</p>
              )}
            </form>
          </div>

          {/* ── Right: illustration ──────────────────────────────────────── */}
          <div className='lr-card-visual' aria-hidden='true'>
            <img src='/media/tfi/logo.webp' alt='' />
          </div>
        </motion.div>

        <div className='lr-footer'>
          <LanguageToggle toggleBtnClass='lr-lang-btn' />
          <span>{VERSION_APP}</span>
        </div>
      </div>
    </div>
  )
}
